/*
 * CrashReport.jsx
 * Copyright (c) Harman International Industries, Incorporated, 2024 - 2025
 * All rights reserved.
 */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { fetchSubmissionDetails } from "../../../Services/redux/Application-details/ApplicationDetailsActions";
import { fetchApplicationDetails } from "../../../Services/redux/appId-details/AppIdDeatilsActions";
import {
  Backdrop,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Snackbar,
  Container,
} from "@mui/material";
import Topheader from "../../../Layout/header/header";
import ApplicationDetailsSideMenu from "../../ApplicationDetailsSideMenu/ApplicationDetailsSideMenu";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format, subDays } from "date-fns";
import APIManager from "../../../Services/ApiManager/ApiManager";
import { BarChart } from "@mui/x-charts";
import { axisClasses } from "@mui/x-charts/ChartsAxis";
import Moment from "moment";
import { chartsGridClasses } from "@mui/x-charts/ChartsGrid";
import PopupDialog from "../../PopupDialog";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { DrawerFilter } from "./DrawerFilter";

export default function CrashReport() {
  const { appId, submissionId } = useParams();
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [selectedVersions, setSelectedVersions] = useState([]);
  const [selectedMakes, setSelectedMakes] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [loadBarChartData, setLoadBarChartData] = useState(false);

  const [crashOccurenceCount, setCrashOccurenceCount] = useState(0);
  const [affectedUsersCount, setAffectedUsersCount] = useState(0);
  const [barObjData, setBarObjData] = useState([]);
  const [stacktraceObjData, setStacktraceObjData] = useState({});
  const [typeOfCrashReport, setTypeOfCrashReport] = useState({});
  const [showTypeOfCrashCard, setShowTypeOfCrashCard] = useState(false);
  const [showStackTraceCard, setShowStackTraceCard] = useState(false);
  const [viewReportData, setViewReportData] = useState({});
  const [totalCrashValue, setTotalCrashValue] = useState(0);
  const [crashMakesObj, setCrashMakesObj] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const defaultStartDate = subDays(new Date(), 30);
  const currentDate = new Date();
  const currentMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  );
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(currentDate);

  const [componentName, setComponentName] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [openFilter, setOpenFilter] = useState(false);

  const appData = useSelector(
    (state) => state.applicationDetails.applicationDetails
  );

  const submissionData = useSelector(
    (state) => state.submissionDetails.submissionDetails
  );

  const allSubmissionsData = useSelector(
    (state) => state.applicationAllSubmissions.submissions
  );
  const subBinaryData = submissionData.binary;

  const callNewSubmissionId = (submissionId) => {
    dispatch(fetchSubmissionDetails(appId, submissionId));
  };

  const backToApplications = () => {
    navigate(`/ui/developer/applications/${appData.id}`, {
      state: {
        teamId: sessionStorage.getItem("teamId"),
        submissionId: submissionId,
      },
    });
  };

  const handlePopUpClose = () => {
    setOpenErrorPopup(false);
  };

  const toggleDrawer = (newOpen) => () => {
    setOpenFilter(newOpen);
  };

  async function getDashboardChartData(packageName, data) {
    setLoader(true);
    setBarObjData([]);
    try {
      
      const [crashOverview, crashStackTrace] = await Promise.all([
        APIManager.postSellerCrashOverviewData(
          packageName,
          data,
          format(startDate, "yyyy-MM-dd"),
          format(endDate, "yyyy-MM-dd")
        ),
        APIManager.postSellerCrashStackTraceData(
          packageName,
          data,
          format(startDate, "yyyy-MM-dd"),
          format(endDate, "yyyy-MM-dd")
        ),
      ]);

      setCrashOccurenceCount(crashOverview.totalCrashCount);
      setAffectedUsersCount(crashOverview.totalAffectedUsers);

      if (crashOverview.crashCountPerDay.length > 0) {
        const crashPerDayObj = crashOverview.crashCountPerDay.map((crashVal) => ({
          formatteddate: Moment(crashVal.date).format("DD MMM"),
          numberOfCrashes: crashVal.numberOfCrashes,
          date: crashVal.date,
        }));
        setBarObjData(crashPerDayObj);
      }

      setLoadBarChartData(true);
      setStacktraceObjData(crashStackTrace);
      setLoader(false);
    } catch (error) {
          setComponentName("appError");
          setErrorMessage(error.message);
          if (error.message === "jwt expired") {
            setHeaderMessage("401 Token Expired");
          } else {
            setHeaderMessage("Error");
          }
          setOpenErrorPopup(true);
          setLoader(false);
        
    }
    finally{
      setLoader(false)
    }
  }

  const barClickHandler = (event, data) => {
    setShowTypeOfCrashCard(true);
    setTypeOfCrashReport(stacktraceObjData[data.dataIndex]);
  };

  const handleViewStackTraceReport = (crashObj, crashType) => {
    setShowStackTraceCard(true);

    let crashReportCheck = crashObj.crashStackTracePerCrashType.find(
      (c) => c.crashType === crashType
    );

    setCrashMakesObj(crashObj.crashesPerMake[crashType]);

    setTotalCrashValue(crashReportCheck.crashStackTrace.length);
    setViewReportData(crashReportCheck);
  };

  const handleNext = (totalValue) => {
    setCurrentIndex((prevIndex) => Math.min(prevIndex + 1, totalValue - 1));
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => Math.max(prevIndex - 1, 0));
  };

  const handleCopyClipBoardContent = () => {
    navigator.clipboard.writeText(viewReportData.crashStackTrace[currentIndex]);
    setOpenSnackbar(true);
    setSnackMsg(`Copied to Clipboard`);
  };

  const handleFilterApiCalls = (props) => {
    setOpenFilter(false);
    getDashboardChartData(subBinaryData.packageName, props);
  };

  useEffect(() => {
    setLoader(true);
    if (appId !== undefined) {
      dispatch(fetchApplicationDetails(appId));
      dispatch(fetchSubmissionDetails(appId, submissionId));
      setLoader(false);
    }
  }, []);

  useEffect(() => {
    if (subBinaryData !== undefined && startDate && endDate) {
      APIManager.getSellerDashboardOverviewData(subBinaryData.packageName)
        .then((json) => {
          setSelectedCountries(json.countries);
          setSelectedMakes(json.makes);

          let data = {
            selectedVersions: json.app_versions,
            selectedCountries: json.countries,
            selectedMakes: json.makes,
          };

          getDashboardChartData(subBinaryData.packageName, data);
        })
        .catch((error) => {
          setComponentName("appError");
          setErrorMessage(error.message);
          if (error.message === "jwt expired") {
            setHeaderMessage("401 Token Expired");
          } else {
            setHeaderMessage("Error");
          }
          setOpenErrorPopup(true);
          setLoader(false);
        });
    } else {
      setLoader(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (allSubmissionsData && allSubmissionsData.length > 0) {
      let versions = allSubmissionsData.map((ele) => {
        return ele.versionName;
      });
      setSelectedVersions(versions);
    }
  }, [allSubmissionsData]);

  return (
    <>
      {loader && (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <Topheader />
      <div className="container-my-release">
        <div
          className="side-menu-my-release"
          style={{ marginTop: "69px", marginLeft: "33px", width: "16%" }}
        >
          <button
            type="button"
            className="arrow-back"
            onClick={backToApplications}
            style={{ background: "none", border: "none", padding: 0 }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <ArrowBackIcon style={{ color: " #717273" }} />
            </div>
            <div className="my-apps">Back To App Details</div>
          </button>
          {appData && Object.keys(appData).length > 0 && (
            <ApplicationDetailsSideMenu
              appData={appData}
              callNewSubmissionId={callNewSubmissionId}
            />
          )}
        </div>
        <Container maxWidth="lg">
            <div className="dashboard-section" id="dashboard">
              <div
                className="listing-header-wrapper-all-release"
                style={{
                  borderBottom: "1px solid  #12141533",
                  paddingTop: "7px",
                  marginTop: "9px",
                  height: "75px",
                  position: "fixed",
                  zIndex: "999",
                  backgroundColor: "white",
                  width: "880px",
                }}
              >
                {submissionData && Object.keys(submissionData).length > 0 && (
                  <div
                    className="app-icon-my-release"
                    style={{ marginRight: "4px" }}
                  >
                    <div className="icon-name-dashboard">
                      <img
                        className="appIconCls"
                        src={submissionData.icon.url}
                        alt={submissionData.fileName}
                        width={50}
                        height={50}
                      />
                      {
                        <div className="name-status name-status-dashboard">
                          <span className="application-name">
                            {appData.name}
                          </span>
                        </div>
                      }

                      <div className="timeRangeCls">
                        <span className="dateRangeTextCls">Date Range</span>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            label="Start Date"
                            value={startDate}
                            onChange={(newValue) => setStartDate(newValue)}
                            maxDate={currentDate}
                            slotProps={{ textField: { size: "small" } }}
                            views={["year", "month", "day"]}
                          />

                          <DatePicker
                            label="End Date"
                            value={endDate}
                            onChange={(newValue) => setEndDate(newValue)}
                            minDate={startDate || currentMonth}
                            slotProps={{ textField: { size: "small" } }}
                            views={["year", "month", "day"]}
                          />
                        </LocalizationProvider>

                        <FilterAltOutlinedIcon
                          color="action"
                          className="filterIconCls"
                          onClick={toggleDrawer(true)}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div>&nbsp;</div>
              {loadBarChartData && (
                <div className="carshOverviewCls" style={{ marginTop: "90px" }}>
                  <div>
                    <Card
                      sx={{
                        width: 150,
                        height: 140,
                        boxShadow: "none",
                        border: "1px solid #12141533",
                      }}
                    >
                      <CardHeader
                        className="crashReportCardHeader"
                        title="Crash Occurance"
                        disableTypography
                      />
                      <Divider />
                      <CardContent
                        sx={{
                          textAlign: "center",
                          paddingTop: "26px",
                          fontWeight: "500",
                          fontSize: "30px",
                        }}
                      >
                        {crashOccurenceCount}
                      </CardContent>
                    </Card>
                    <div>&nbsp;</div>
                    <Card
                      sx={{
                        width: 150,
                        height: 140,
                        boxShadow: "none",
                        border: "1px solid #12141533",
                      }}
                    >
                      <CardHeader
                        className="crashReportCardHeader"
                        title="Affected Users"
                        disableTypography
                      />
                      <Divider />
                      <CardContent
                        sx={{
                          textAlign: "center",
                          paddingTop: "26px",
                          fontWeight: "500",
                          fontSize: "30px",
                        }}
                      >
                        {affectedUsersCount}
                      </CardContent>
                    </Card>
                  </div>

                  <div>
                    <Card
                      sx={{
                        width: 722,
                        height: 300,
                        boxShadow: "none",
                        border: "1px solid #12141533",
                      }}
                    >
                      <CardHeader
                        className="crashReportCardHeader"
                        title="No of Crashes"
                        action={
                          <>
                            <span className="totalCls">Total</span>
                            <div className="totalCountCls">0</div>
                          </>
                        }
                        disableTypography
                      />
                      <Divider />
                      <CardContent
                        sx={{
                          textAlign: "center",
                          padding: "0px !important",
                          marginTop: "-25px",
                        }}
                      >
                        {loadBarChartData && (
                          <BarChart
                            dataset={barObjData}
                            barLabel="value"
                            grid={{ horizontal: true }}
                            onItemClick={barClickHandler}
                            xAxis={[
                              {
                                scaleType: "band",
                                dataKey: "formatteddate",
                                colorMap: {
                                  type: "piecewise",
                                  thresholds: [],
                                  colors: ["#0476E2"],
                                },
                              },
                            ]}
                            series={[
                              {
                                dataKey: "numberOfCrashes",
                              },
                            ]}
                            {...chartSetting}
                            sx={{
                              "& .MuiBarElement-root": {
                                minwidth: "20px !important",
                              },
                              [`& .${chartsGridClasses.line}`]: {
                                strokeDasharray: "5 3",
                                strokeWidth: 2,
                              },
                            }}
                          />
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}
              <div>&nbsp;</div>
              {showTypeOfCrashCard && (
                <div>
                  <Card
                    sx={{
                      width: 890,
                      height: 200,
                      boxShadow: "none",
                      border: "1px solid #12141533",
                    }}
                  >
                    <CardHeader
                      className="stacktraceCardHeader"
                      title="Types Of Crash"
                      disableTypography
                    />

                    <CardContent
                      sx={{
                        padding: "0px !important",
                        paddingLeft: "17px !important",
                      }}
                    >
                      <div className="typesOfCrashSubHeaderCls">
                        Crashed on:{" "}
                        <span className="crashDateCls">
                          {Moment(typeOfCrashReport.date).format("DD MMM YYYY")}
                        </span>
                      </div>
                      <div>&nbsp;</div>
                      <div className="crashTypesCardDivCls">
                        {typeOfCrashReport.crashesByCrashType.map(
                          (crash, index) => (
                            <Card
                              key={crash.crashType}
                              sx={{
                                minWidth: 136,
                                height: 80,
                                boxShadow: "none",
                                border: "1px solid #12141533",
                                background: "#1214150D",
                              }}
                            >
                              <div className="typeCrashCardcls">
                                <span className="typeContent1cls">
                                  {crash.crashCount}
                                </span>
                                <div className="typeContent2cls">
                                  <span className="crashTypeNameCls">
                                    {crash.crashType
                                      .split("_")
                                      .pop()
                                      .toUpperCase()}
                                  </span>

                                  <button
                                    type="button"
                                    className="reportLinkCls"
                                    onClick={() =>
                                      handleViewStackTraceReport(
                                        typeOfCrashReport,
                                        crash.crashType
                                      )
                                    }
                                    style={{ background: "none", border: "none", padding: 0 }}
                                  >
                                    View Report
                                  </button>
                                </div>
                              </div>
                            </Card>
                          )
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
              <div>&nbsp;</div>
              {showStackTraceCard && (
                <div>
                  <Card
                    sx={{
                      width: 890,
                      minHeight: 400,
                      boxShadow: "none",
                      border: "1px solid #12141533",
                    }}
                  >
                    <CardHeader
                      className="stacktraceCardHeader"
                      title="Stack Trace Report"
                      disableTypography
                    />

                    <CardContent
                      sx={{
                        padding: "0px !important",
                        paddingLeft: "17px !important",
                      }}
                    >
                      <div className="stackTraceSubTextCls">
                        <span>
                          Crashed No. {currentIndex + 1} of {totalCrashValue}
                        </span>
                        <span>
                          <Button
                            className="prevBtnTextCls"
                            onClick={handlePrev}
                            disabled={currentIndex === 0}
                          >
                            <svg
                              width="9"
                              height="12"
                              viewBox="0 0 9 12"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M6.7998 12L0.799805 6L6.7998 0L8.1998 1.4L3.5998 6L8.1998 10.6L6.7998 12Z"
                                fill="#121415"
                                fillOpacity="0.6"
                              />
                            </svg>
                            &nbsp; Previous Crash
                          </Button>
                          &nbsp;{" "}
                          <span
                            style={{ fontSize: "14px", color: "#000000DE" }}
                          >
                            {currentIndex + 1}/{totalCrashValue}
                          </span>{" "}
                          &nbsp;
                          <Button
                            className="nextBtnTextCls"
                            onClick={(event) => handleNext(totalCrashValue)}
                            disabled={currentIndex === totalCrashValue - 1}
                          >
                            Next Crash &nbsp;
                            <svg
                              width="9"
                              height="12"
                              viewBox="0 0 9 12"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M2.2002 12L8.2002 6L2.2002 0L0.800195 1.4L5.4002 6L0.800195 10.6L2.2002 12Z"
                                fill="#121415"
                                fillOpacity="0.6"
                              />
                            </svg>
                          </Button>
                        </span>
                      </div>
                      <div>&nbsp;</div>
                      <div className="typesOfCrashSubHeaderCls">
                        {"Total No. of Crashes: "}
                        <span style={{ fontWeight: "600", color: "#121415" }}>
                          {totalCrashValue}
                        </span>
                        {". Makes: "}
                        {crashMakesObj.map((makes) => (
                          <span className="makesListCls" key={makes.make}>
                            <span style={{ fontWeight: "600", color: "#121415" }}>
                              {makes.make}
                            </span>
                            {" "}
                            <svg
                              width="11"
                              height="8"
                              viewBox="0 0 11 8"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M6.75 7.75L5.875 6.84375L8.09375 4.625H0.5V3.375H8.09375L5.875 1.15625L6.75 0.25L10.5 4L6.75 7.75Z"
                                fill="#B8B9B9"
                              />
                            </svg>
                            {" "}
                            <span style={{ fontWeight: "600", color: "#121415" }}>
                              {makes.crashes} {makes.crashes === 1 ? "Crash" : "Crashes"}
                            </span>
                          </span>
                        ))}
                      </div>
                      <div className="crashTypesCardDivCls1">
                        <pre className="stackTraceCls">
                          {JSON.stringify(viewReportData.crashStackTrace)}{" "}
                          {viewReportData.crashStackTrace[currentIndex]}
                        </pre>
                        <button
                          type="button"
                          style={{ paddingTop: "12px", cursor: "pointer" }}
                          onClick={handleCopyClipBoardContent}
                          className=""
                          aria-label="Copy stack trace"
                          title="Copy stack trace"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              handleCopyClipBoardContent();
                            }
                          }}
                        >
                          <svg
                            width="69"
                            height="24"
                            viewBox="0 0 69 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              x="0.700195"
                              width="68"
                              height="24"
                              rx="4"
                              fill="#121415"
                              fillOpacity="0.95"
                            />
                            <path
                              d="M14.7004 16.8C14.2604 16.8 13.8837 16.6433 13.5704 16.33C13.2571 16.0167 13.1004 15.64 13.1004 15.2V5.6C13.1004 5.16 13.2571 4.78333 13.5704 4.47C13.8837 4.15667 14.2604 4 14.7004 4H21.9004C22.3404 4 22.7171 4.15667 23.0304 4.47C23.3437 4.78333 23.5004 5.16 23.5004 5.6V15.2C23.5004 15.64 23.3437 16.0167 23.0304 16.33C22.7171 16.6433 22.3404 16.8 21.9004 16.8H14.7004ZM11.5004 20C11.0604 20 10.6837 19.8433 10.3704 19.53C10.0571 19.2167 9.90039 18.84 9.90039 18.4V7.2H11.5004V18.4H20.3004V20H11.5004Z"
                              fill="white"
                            />
                            <path
                              d="M36.6064 13.2227H38.0713C38.0244 13.7812 37.8682 14.2793 37.6025 14.7168C37.3369 15.1504 36.9639 15.4922 36.4834 15.7422C36.0029 15.9922 35.4189 16.1172 34.7314 16.1172C34.2041 16.1172 33.7295 16.0234 33.3076 15.8359C32.8857 15.6445 32.5244 15.375 32.2236 15.0273C31.9229 14.6758 31.6924 14.252 31.5322 13.7559C31.376 13.2598 31.2979 12.7051 31.2979 12.0918V11.3828C31.2979 10.7695 31.3779 10.2148 31.5381 9.71875C31.7021 9.22266 31.9365 8.79883 32.2412 8.44727C32.5459 8.0918 32.9111 7.82031 33.3369 7.63281C33.7666 7.44531 34.249 7.35156 34.7842 7.35156C35.4639 7.35156 36.0381 7.47656 36.5068 7.72656C36.9756 7.97656 37.3389 8.32227 37.5967 8.76367C37.8584 9.20508 38.0186 9.71094 38.0771 10.2812H36.6123C36.5732 9.91406 36.4873 9.59961 36.3545 9.33789C36.2256 9.07617 36.0342 8.87695 35.7803 8.74023C35.5264 8.59961 35.1943 8.5293 34.7842 8.5293C34.4482 8.5293 34.1553 8.5918 33.9053 8.7168C33.6553 8.8418 33.4463 9.02539 33.2783 9.26758C33.1104 9.50977 32.9834 9.80859 32.8975 10.1641C32.8154 10.5156 32.7744 10.918 32.7744 11.3711V12.0918C32.7744 12.5215 32.8115 12.9121 32.8857 13.2637C32.9639 13.6113 33.0811 13.9102 33.2373 14.1602C33.3975 14.4102 33.6006 14.6035 33.8467 14.7402C34.0928 14.877 34.3877 14.9453 34.7314 14.9453C35.1494 14.9453 35.4873 14.8789 35.7451 14.7461C36.0068 14.6133 36.2041 14.4199 36.3369 14.166C36.4736 13.9082 36.5635 13.5938 36.6064 13.2227ZM38.9971 12.9004V12.7656C38.9971 12.3086 39.0635 11.8848 39.1963 11.4941C39.3291 11.0996 39.5205 10.7578 39.7705 10.4688C40.0244 10.1758 40.333 9.94922 40.6963 9.78906C41.0635 9.625 41.4775 9.54297 41.9385 9.54297C42.4033 9.54297 42.8174 9.625 43.1807 9.78906C43.5479 9.94922 43.8584 10.1758 44.1123 10.4688C44.3662 10.7578 44.5596 11.0996 44.6924 11.4941C44.8252 11.8848 44.8916 12.3086 44.8916 12.7656V12.9004C44.8916 13.3574 44.8252 13.7812 44.6924 14.1719C44.5596 14.5625 44.3662 14.9043 44.1123 15.1973C43.8584 15.4863 43.5498 15.7129 43.1865 15.877C42.8232 16.0371 42.4111 16.1172 41.9502 16.1172C41.4854 16.1172 41.0693 16.0371 40.7021 15.877C40.3389 15.7129 40.0303 15.4863 39.7764 15.1973C39.5225 14.9043 39.3291 14.5625 39.1963 14.1719C39.0635 13.7812 38.9971 13.3574 38.9971 12.9004ZM40.4092 12.7656V12.9004C40.4092 13.1855 40.4385 13.4551 40.4971 13.709C40.5557 13.9629 40.6475 14.1855 40.7725 14.377C40.8975 14.5684 41.0576 14.7188 41.2529 14.8281C41.4482 14.9375 41.6807 14.9922 41.9502 14.9922C42.2119 14.9922 42.4385 14.9375 42.6299 14.8281C42.8252 14.7188 42.9854 14.5684 43.1104 14.377C43.2354 14.1855 43.3271 13.9629 43.3857 13.709C43.4482 13.4551 43.4795 13.1855 43.4795 12.9004V12.7656C43.4795 12.4844 43.4482 12.2188 43.3857 11.9688C43.3271 11.7148 43.2334 11.4902 43.1045 11.2949C42.9795 11.0996 42.8193 10.9473 42.624 10.8379C42.4326 10.7246 42.2041 10.668 41.9385 10.668C41.6729 10.668 41.4424 10.7246 41.2471 10.8379C41.0557 10.9473 40.8975 11.0996 40.7725 11.2949C40.6475 11.4902 40.5557 11.7148 40.4971 11.9688C40.4385 12.2188 40.4092 12.4844 40.4092 12.7656ZM47.4932 10.8789V18.4375H46.0811V9.66016H47.3818L47.4932 10.8789ZM51.624 12.7715V12.8945C51.624 13.3555 51.5693 13.7832 51.46 14.1777C51.3545 14.5684 51.1963 14.9102 50.9854 15.2031C50.7783 15.4922 50.5225 15.7168 50.2178 15.877C49.9131 16.0371 49.5615 16.1172 49.1631 16.1172C48.7686 16.1172 48.4229 16.0449 48.126 15.9004C47.833 15.752 47.585 15.543 47.3818 15.2734C47.1787 15.0039 47.0146 14.6875 46.8896 14.3242C46.7686 13.957 46.6826 13.5547 46.6318 13.1172V12.6426C46.6826 12.1777 46.7686 11.7559 46.8896 11.377C47.0146 10.998 47.1787 10.6719 47.3818 10.3984C47.585 10.125 47.833 9.91406 48.126 9.76562C48.4189 9.61719 48.7607 9.54297 49.1514 9.54297C49.5498 9.54297 49.9033 9.62109 50.2119 9.77734C50.5205 9.92969 50.7803 10.1484 50.9912 10.4336C51.2021 10.7148 51.3604 11.0547 51.4658 11.4531C51.5713 11.8477 51.624 12.2871 51.624 12.7715ZM50.2119 12.8945V12.7715C50.2119 12.4785 50.1846 12.207 50.1299 11.957C50.0752 11.7031 49.9893 11.4805 49.8721 11.2891C49.7549 11.0977 49.6045 10.9492 49.4209 10.8438C49.2412 10.7344 49.0244 10.6797 48.7705 10.6797C48.5205 10.6797 48.3057 10.7227 48.126 10.8086C47.9463 10.8906 47.7959 11.0059 47.6748 11.1543C47.5537 11.3027 47.46 11.4766 47.3936 11.6758C47.3271 11.8711 47.2803 12.084 47.2529 12.3145V13.4512C47.2998 13.7324 47.3799 13.9902 47.4932 14.2246C47.6064 14.459 47.7666 14.6465 47.9736 14.7871C48.1846 14.9238 48.4541 14.9922 48.7822 14.9922C49.0361 14.9922 49.2529 14.9375 49.4326 14.8281C49.6123 14.7188 49.7588 14.5684 49.8721 14.377C49.9893 14.1816 50.0752 13.957 50.1299 13.7031C50.1846 13.4492 50.2119 13.1797 50.2119 12.8945ZM54.5947 15.3086L56.3174 9.66016H57.8291L55.2861 16.9668C55.2275 17.123 55.1514 17.293 55.0576 17.4766C54.9639 17.6602 54.8408 17.834 54.6885 17.998C54.54 18.166 54.3545 18.3008 54.1318 18.4023C53.9092 18.5078 53.6396 18.5605 53.3232 18.5605C53.1982 18.5605 53.0771 18.5488 52.96 18.5254C52.8467 18.5059 52.7393 18.4844 52.6377 18.4609L52.6318 17.3828C52.6709 17.3867 52.7178 17.3906 52.7725 17.3945C52.8311 17.3984 52.8779 17.4004 52.9131 17.4004C53.1475 17.4004 53.3428 17.3711 53.499 17.3125C53.6553 17.2578 53.7822 17.168 53.8799 17.043C53.9814 16.918 54.0674 16.75 54.1377 16.5391L54.5947 15.3086ZM53.6221 9.66016L55.1279 14.4062L55.3799 15.8945L54.4014 16.1465L52.0986 9.66016H53.6221Z"
                              fill="white"
                            />
                          </svg>
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
        </Container>
      </div>
      <PopupDialog
        openErrorPopup={openErrorPopup}
        errorMessage={errorMessage}
        headerMessage={headerMessage}
        componentName={componentName}
        onClose={handlePopUpClose}
      />
      <DrawerFilter
        openFilter={openFilter}
        toggleDrawer={toggleDrawer}
        selectedVersions={selectedVersions}
        selectedMakes={selectedMakes}
        selectedCountries={selectedCountries}
        handleFilterApiCalls={handleFilterApiCalls}
      />
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        message={snackMsg}
      />
    </>
  );
}

const chartSetting = {
  yAxis: [
    {
      label: "No. of Crashes",
    },
  ],
  width: 750,
  height: 260,
  sx: {
    [`.${axisClasses.left} .${axisClasses.label}`]: {
      transform: "translate(-10px, 0)",
    },
  },
};
